import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { ParsedMarkdownTable } from "./parseMarkdownTable";

type UniversityLessonTableProps = {
  headers: string[];
  rows: string[][];
  className?: string;
};

/** Lightweight inline markdown for table cells (**bold**, `code`). */
function renderCellText(raw: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(raw)) !== null) {
    if (match.index > last) {
      parts.push(raw.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      parts.push(
        <strong key={`b-${key}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      parts.push(
        <code
          key={`c-${key}`}
          className="rounded px-1 py-0.5 font-mono text-[0.85em] bg-muted"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      parts.push(token);
    }
    key += 1;
    last = match.index + token.length;
  }

  if (last < raw.length) {
    parts.push(raw.slice(last));
  }

  return parts.length > 0 ? parts : raw;
}

export function UniversityLessonTable({
  headers,
  rows,
  className,
}: UniversityLessonTableProps): React.ReactElement {
  return (
    <div
      data-university-lesson-table=""
      className={cn("my-5 w-full min-w-0 overflow-x-auto rounded-[6px] border border-border", className)}
    >
      <Table className="min-w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {headers.map((header, index) => (
              <TableHead
                key={`h-${index}`}
                className="h-auto min-h-10 whitespace-normal bg-muted/40 py-2.5 font-[family-name:var(--font-primary)] text-[color:var(--brand-tropical-surf)]"
              >
                {renderCellText(header)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={`r-${rowIndex}`}>
              {headers.map((_, colIndex) => (
                <TableCell
                  key={`c-${rowIndex}-${colIndex}`}
                  className="whitespace-normal py-2.5 align-top font-['IBM_Plex_Sans',sans-serif] text-sm text-muted-foreground"
                >
                  {renderCellText(row[colIndex] ?? "")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function UniversityLessonTableFromParsed({
  table,
  className,
}: {
  table: ParsedMarkdownTable;
  className?: string;
}): React.ReactElement {
  return (
    <UniversityLessonTable headers={table.headers} rows={table.rows} className={className} />
  );
}
